/**
 * @file JoinScreen.jsx
 * @location src/components/auth/JoinScreen.jsx
 * @role 로그인됐지만 아직 팀에 속하지 않은 사용자 — 팀 생성 또는 초대 수락.
 *
 *  create 모드: 새 워크스페이스 생성(본인=매니저/오너). 이름만 입력 (회사명 불요 razor ㉒).
 *  invite 모드: 매니저가 발급한 INV-코드 + 본인 정보로 기존 팀 합류.
 *  로그아웃은 「다른 계정으로 다시 시작」 출구 — 주 CTA 와 경쟁하지 않게 상단 텍스트 링크로 격하.
 */
import { useState } from 'react';
import { normalizeInviteCode } from '../../utils/inviteCode.js';
import { localizeAuthError } from '../../integrations/supabase/errorMessage.js';

export function JoinScreen({ email, initialCode = '', onCreateTeam, onAcceptInvite, onSignOut }) {
  const [mode, setMode] = useState(initialCode ? 'invite' : 'create');  // 'create' | 'invite'
  const [name, setName] = useState('');
  const [code, setCode] = useState(initialCode);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const run = async () => {
    const nm = name.trim();
    if (!nm || busy) return;
    setBusy(true); setErr('');
    try {
      if (mode === 'create') {
        await onCreateTeam(nm);
      } else {
        await onAcceptInvite({ code: normalizeInviteCode(code), name: nm });
      }
    } catch (e) {
      setErr(localizeAuthError(e, '처리에 실패했습니다. 잠시 후 다시 시도해 주세요.'));
      setBusy(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="modal auth-modal">
        <div className="mhd"><span className="mtit">온점에 오신 것을 환영합니다</span></div>
        <div className="mbody">
          <p className="auth-meta">
            <span className="addr">{email}</span>
            <button type="button" className="auth-signout" onClick={onSignOut} disabled={busy}>
              로그아웃
            </button>
          </p>

          <div className="seg" role="tablist">
            <button
              className={`seg-opt${mode === 'create' ? ' on' : ''}`}
              role="tab" aria-selected={mode === 'create'}
              onClick={() => setMode('create')}
            >
              새 팀 만들기
            </button>
            <button
              className={`seg-opt${mode === 'invite' ? ' on' : ''}`}
              role="tab" aria-selected={mode === 'invite'}
              onClick={() => setMode('invite')}
            >
              초대 코드로 참여
            </button>
          </div>

          {mode === 'invite' && (
            <div>
              <label className="flabel" htmlFor="join-code">초대 코드</label>
              <input
                id="join-code" className="finput" value={code}
                onChange={e => setCode(e.target.value)}
                placeholder="INV-XXXX"
              />
            </div>
          )}

          <div>
            <label className="flabel" htmlFor="join-name">이름</label>
            <input
              id="join-name" className="finput" value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) run(); }}
              placeholder="예: 표한결" autoFocus
            />
          </div>

          {err && <p className="auth-err">{err}</p>}
        </div>
        <div className="mfoot">
          <button className="btnp" onClick={run} disabled={!name.trim() || busy}>
            {busy ? '처리 중…' : (mode === 'create' ? '팀 만들기' : '참여')}
          </button>
        </div>
      </div>
    </div>
  );
}
